import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, CardContent, CardFooter, CardHeader } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { GetOrderHistory } from '../../hooks/reactQuery';
import { formatCurrency } from '../../Utils';
import { toast } from 'react-hot-toast';

function OrdersPage({ inline = false, onCloseInline = null }) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const merchantId = user?.merchantId || user?.restaurant_id || localStorage.getItem('merchant_id');
  const customerId = user?.customer_id || localStorage.getItem('customer_id');
  const { data: orderData, isLoading } = GetOrderHistory(merchantId, customerId);
  const orders = Array.isArray(orderData) ? orderData : [];
  const hasAccountContext = Boolean(merchantId && customerId);

  const totalOrders = orders.length;
  const completedOrders = orders.filter(order => order.status === 'Completed' || order.status === 'delivered').length;
  const totalSpent = orders.reduce((acc, order) => acc + parseFloat(order.total || order.total_amount || 0), 0);

  const handleReorder = (items) => {
    toast.info('Reorder coming soon');
  };

  const content = (
    <div className={`${inline ? 'min-h-full' : 'min-h-screen'} bg-background p-4`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => inline ? onCloseInline() : navigate(-1)}
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-semibold ml-4">Dining History</h1>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <h2 className="text-md font-semibold">Order Summary</h2>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex-1 min-w-[150px]">
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Total Orders:</span>
                  <span className="font-semibold">{totalOrders}</span>
                </div>
                <Separator />
              </div>
              <div className="flex-1 min-w-[150px]">
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Completed:</span>
                  <span className="font-semibold">{completedOrders}</span>
                </div>
                <Separator />
              </div>
              <div className="flex-1 min-w-[150px]">
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Total Spent:</span>
                  <span className="font-semibold">{formatCurrency(totalSpent)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {!hasAccountContext ? (
          <div className="text-center py-12 px-4">
            <p className="text-gray-700 font-semibold">No account order history yet</p>
            <p className="text-gray-500 text-sm mt-2">
              Sign in or create an account with the same phone number to keep your orders and points together.
            </p>
          </div>
        ) : isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No orders yet</p>
          </div>
        ) : (
          orders.map((order) => (
            <Card key={order.id || order.order_id} className="mb-6">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-md font-semibold">Order #{order.order_number || order.id}</h2>
                    <p className="text-muted-foreground">Date: {order.created_at ? new Date(order.created_at).toLocaleDateString() : order.date || '-'}</p>
                    {order.restaurant_name && <p className="text-muted-foreground">Restaurant: {order.restaurant_name}</p>}
                  </div>
                  <Badge variant={(order.status === 'Completed' || order.status === 'delivered') ? 'success' : 'warning'}>
                    {order.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <h3 className="text-md font-semibold mb-2">Items:</h3>
                <ul>
                  {(order.items || order.order_items || []).map((item) => (
                    <li key={item.id || item.item_id} className="flex items-center justify-between py-2">
                      <div className="flex items-center">
                        {item.image && <img src={item.image} alt={item.name || item.item_name} className="w-10 h-10 rounded-md mr-4 object-cover" />}
                        <span>{item.name || item.item_name} x {item.quantity || item.qty}</span>
                      </div>
                      <span>{formatCurrency(item.price || item.unit_price || 0)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="flex flex-col">
                <div className="flex justify-between items-center w-full mb-4">
                  <span className="font-semibold">Total:</span>
                  <span className="font-semibold">{formatCurrency(order.total || order.total_amount || 0)}</span>
                </div>
                <Button
                  onClick={() => handleReorder(order.items || order.order_items)}
                  className="w-full"
                  variant="default"
                >
                  Reorder
                </Button>
              </CardFooter>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div 
          className="absolute inset-0 bg-black/40 transition-opacity"
          onClick={onCloseInline}
        />
        <div className="relative w-full max-w-md h-full bg-background shadow-2xl overflow-y-auto animate-in slide-in-from-right">
          {content}
        </div>
      </div>
    );
  }

  return content;
}

export default OrdersPage;
